import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Save, X, Plane, Search, Info } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AddAircraft = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [aircraftTypes, setAircraftTypes] = useState([]);
  const [typeSuggestions, setTypeSuggestions] = useState([]);
  const [showTypeSuggestions, setShowTypeSuggestions] = useState(false);
  const [searchingTypes, setSearchingTypes] = useState(false);
  const [autoPopulateStatus, setAutoPopulateStatus] = useState(null);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues
  } = useForm({
    defaultValues: {
      engine_type: 'piston',
      complex: false,
      high_performance: false,
      retractable_gear: false,
      constant_speed_prop: false,
      glass_cockpit: false,
      pressurized: false,
      turbo_charged: false
    }
  });

  const watchManufacturer = watch('manufacturer');
  const watchModel = watch('model');

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  };

  // Load aircraft types on component mount
  useEffect(() => {
    const loadAircraftTypes = async () => {
      try {
        const response = await axios.get('/api/aircraft-types', getAuthHeaders());
        setAircraftTypes(response.data);
        console.log('Loaded aircraft types:', response.data);
      } catch (error) {
        console.error('Error loading aircraft types:', error);
      }
    };
    loadAircraftTypes();
  }, []);

  // Auto-populate from database when manufacturer and model are filled
  const autoPopulateFromDatabase = useCallback(async (manufacturer, model) => {
    if (!manufacturer || !model || manufacturer.length < 3 || model.length < 2) {
      setAutoPopulateStatus(null);
      return;
    }

    try {
      console.log('Auto-populating for:', manufacturer, model); // Debug log
      const response = await axios.get(
        `/api/aircraft/auto-populate?manufacturer=${encodeURIComponent(manufacturer)}&model=${encodeURIComponent(model)}`,
        getAuthHeaders()
      );

      console.log('Auto-populate response:', response.data); // Debug log

      if (response.data.found) {
        const specs = response.data.specifications;
        const source = response.data.source;
        const confidence = response.data.confidence;

        console.log('Specifications found:', specs); // Debug log

        // Only populate fields that are still at their default values
        const currentValues = getValues();
        console.log('Current form values:', currentValues); // Debug log
        
        // Handle engine type with case conversion
        if (specs.engine_type && currentValues.engine_type === 'piston') {
          const engineType = specs.engine_type.toLowerCase(); // Convert to lowercase
          console.log('Setting engine type from', specs.engine_type, 'to', engineType); // Debug log
          setValue('engine_type', engineType);
        }
        
        if (specs.category && !currentValues.category) {
          console.log('Setting category:', specs.category); // Debug log
          setValue('category', specs.category);
        }
        if (specs.class && !currentValues.class) {
          console.log('Setting class:', specs.class); // Debug log
          setValue('class', specs.class);
        }
        if (specs.complex !== undefined && currentValues.complex === false) {
          console.log('Setting complex:', Boolean(specs.complex)); // Debug log
          setValue('complex', Boolean(specs.complex));
        }
        if (specs.high_performance !== undefined && currentValues.high_performance === false) {
          console.log('Setting high_performance:', Boolean(specs.high_performance)); // Debug log
          setValue('high_performance', Boolean(specs.high_performance));
        }
        if (specs.retractable_gear !== undefined && currentValues.retractable_gear === false) {
          console.log('Setting retractable_gear:', Boolean(specs.retractable_gear)); // Debug log
          setValue('retractable_gear', Boolean(specs.retractable_gear));
        }
        if (specs.constant_speed_prop !== undefined && currentValues.constant_speed_prop === false) {
          console.log('Setting constant_speed_prop:', Boolean(specs.constant_speed_prop)); // Debug log
          setValue('constant_speed_prop', Boolean(specs.constant_speed_prop));
        }
        if (specs.glass_cockpit !== undefined && currentValues.glass_cockpit === false) {
          console.log('Setting glass_cockpit:', Boolean(specs.glass_cockpit)); // Debug log
          setValue('glass_cockpit', Boolean(specs.glass_cockpit));
        }

        // Show success message based on source and confidence
        let message = 'Auto-populated aircraft specifications';
        if (source === 'aircraft_types') {
          message += ' from aircraft database';
        } else {
          message += ` from existing aircraft (${confidence} confidence)`;
        }

        setAutoPopulateStatus({
          type: 'success',
          message,
          confidence,
          source
        });

        // Auto-hide status after 5 seconds
        setTimeout(() => setAutoPopulateStatus(null), 5000);
      } else {
        console.log('No specifications found for', manufacturer, model); // Debug log
        setAutoPopulateStatus(null);
      }
    } catch (error) {
      console.error('Error auto-populating from database:', error);
      setAutoPopulateStatus(null);
    }
  }, [setValue, getValues]);

  // Debounced auto-populate
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (watchManufacturer && watchModel) {
        autoPopulateFromDatabase(watchManufacturer, watchModel);
      }
    }, 1000); // Wait 1 second after user stops typing

    return () => clearTimeout(timeoutId);
  }, [watchManufacturer, watchModel, autoPopulateFromDatabase]);

  // Detect transport-category jets (airliners) by manufacturer or common type-code
  // patterns, so sensible defaults can be applied even when the aircraft_types
  // row itself has incomplete/incorrect characteristic flags.
  const isTransportJet = (typeCode, manufacturer) => {
    const tc = (typeCode || '').toUpperCase();
    const mfr = (manufacturer || '').toUpperCase();

    const transportManufacturers = [
      'BOEING', 'AIRBUS', 'EMBRAER', 'BOMBARDIER',
      'MCDONNELL DOUGLAS', 'MCDONNELL', 'DOUGLAS', 'ATR'
    ];
    if (transportManufacturers.some(m => mfr.includes(m))) return true;

    const transportTypeCodePatterns = [
      /^B7\d{2}/,   // Boeing 707/717/727/737/747/757/767/777/787 (B738, B739, B77W, B788...)
      /^B3\dM/,     // Boeing 737 MAX (B38M, B39M...)
      /^A[123]\d[\dNK]/, // Airbus A300-A380 incl. neo/ICAO codes (A320, A20N, A21N, A35K...)
      /^E1[7-9]\d/, // Embraer E170/175/190/195
      /^E29\d/,     // Embraer E-Jet E2 (E290, E295...)
      /^CRJ/,       // Bombardier CRJ
      /^DC\d/,      // McDonnell Douglas DC-9/10
      /^MD\d/       // MD-80/90/11
    ];
    return transportTypeCodePatterns.some(p => p.test(tc));
  };

  // Search aircraft types as user types
  const searchAircraftTypes = async (query) => {
    if (!query || query.length < 1) {
      setTypeSuggestions([]);
      setShowTypeSuggestions(false);
      return;
    }

    setSearchingTypes(true);
    try {
      // Filter aircraft types based on the query
      const filtered = aircraftTypes.filter(type =>
        type.type_code?.toLowerCase().includes(query.toLowerCase()) ||
        type.manufacturer?.toLowerCase().includes(query.toLowerCase()) ||
        type.model?.toLowerCase().includes(query.toLowerCase())
      );
      
      setTypeSuggestions(filtered.slice(0, 10)); // Limit to 10 suggestions
      setShowTypeSuggestions(true);
    } catch (error) {
      console.error('Error searching aircraft types:', error);
      setTypeSuggestions([]);
    } finally {
      setSearchingTypes(false);
    }
  };

  // Handle aircraft type selection
  const selectAircraftType = (aircraftType) => {
    console.log('Selected aircraft type:', aircraftType); // Debug log
    
    // Set the aircraft type
    setValue('aircraft_type', aircraftType.type_code);
    setValue('aircraft_type_id', aircraftType.id); // Store the ID for reference
    
    // Auto-populate manufacturer and model
    if (aircraftType.manufacturer) {
      setValue('manufacturer', aircraftType.manufacturer);
    }
    if (aircraftType.model) {
      setValue('model', aircraftType.model);
    }
    
    // Auto-populate engine type if available with proper mapping
    if (aircraftType.engine_type) {
      const engineTypeMap = {
        'Jet': 'jet',
        'JET': 'jet',
        'Turbo-fan': 'turbofan',
        'Turbojet': 'turbojet',
        'TURBOJET': 'turbojet',
        'Turboprop': 'turboprop',
        'TURBOPROP': 'turboprop',
        'Piston': 'piston',
        'PISTON': 'piston',
        'Electric': 'electric',
        'ELECTRIC': 'electric'
      };
      
      const mappedEngineType = engineTypeMap[aircraftType.engine_type] || aircraftType.engine_type.toLowerCase();
      console.log('Setting engine type from', aircraftType.engine_type, 'to', mappedEngineType); // Debug log
      setValue('engine_type', mappedEngineType);
    }
    
    // Auto-populate category and class
    if (aircraftType.category) {
      setValue('category', aircraftType.category);
    }
    if (aircraftType.class) {
      setValue('class', aircraftType.class);
    }
    
    // Auto-populate characteristics if available
    if (aircraftType.complex !== undefined) {
      setValue('complex', Boolean(aircraftType.complex));
    }
    if (aircraftType.high_performance !== undefined) {
      setValue('high_performance', Boolean(aircraftType.high_performance));
    }
    if (aircraftType.retractable_gear !== undefined) {
      setValue('retractable_gear', Boolean(aircraftType.retractable_gear));
    }
    if (aircraftType.constant_speed_prop !== undefined) {
      setValue('constant_speed_prop', Boolean(aircraftType.constant_speed_prop));
    }
    if (aircraftType.glass_cockpit !== undefined) {
      setValue('glass_cockpit', Boolean(aircraftType.glass_cockpit));
    }
    
    // Force sensible defaults for transport-category jets (Boeing, Airbus, and
    // other airliner types), regardless of what the aircraft_types row has —
    // these characteristics are true by definition for the whole category.
    if (isTransportJet(aircraftType.type_code, aircraftType.manufacturer)) {
      setValue('engine_type', 'turbofan');
      setValue('high_performance', true);
      setValue('complex', true);
      setValue('retractable_gear', true);
      setValue('constant_speed_prop', false); // jets have no propeller
      setValue('glass_cockpit', true);
    }
    
    // Show success message
    setAutoPopulateStatus({
      type: 'success',
      message: 'Auto-populated from aircraft type database',
      confidence: 'high',
      source: 'aircraft_types'
    });

    setTimeout(() => setAutoPopulateStatus(null), 5000);
    
    // Hide suggestions
    setShowTypeSuggestions(false);
    setTypeSuggestions([]);
  };

  // Hide suggestions when clicking outside
  const handleBlur = () => {
    // Small delay to allow click events on suggestions to fire
    setTimeout(() => {
      setShowTypeSuggestions(false);
    }, 150);
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      await axios.post('/api/aircraft', data, getAuthHeaders());
      toast.success('Aircraft added successfully!');
      navigate('/aircraft');
    } catch (error) {
      console.error('Error adding aircraft:', error);
      if (error.response?.data?.error?.includes('already exists')) {
        toast.error('Aircraft with this tail number already exists');
      } else {
        toast.error('Failed to add aircraft');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <div className="aviation-gradient p-2 rounded-lg">
            <Plane className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Add New Aircraft</h1>
        </div>
        <p className="text-gray-600">
          Add a new aircraft to your fleet
        </p>
      </div>

      {/* Auto-populate status */}
      {autoPopulateStatus && (
        <div className={`mb-6 p-4 rounded-lg border ${
          autoPopulateStatus.type === 'success' 
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-blue-50 border-blue-200 text-blue-800'
        }`}>
          <div className="flex items-center space-x-2">
            <Info className="h-4 w-4" />
            <span className="text-sm font-medium">{autoPopulateStatus.message}</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Basic Aircraft Information */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Aircraft Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tail Number / Aircraft ID *
              </label>
              <input
                type="text"
                {...register('aircraft_id', { 
                  required: 'Aircraft ID is required',
                  pattern: {
                    value: /^[A-Z0-9-]+$/i,
                    message: 'Aircraft ID should contain only letters, numbers, and hyphens'
                  }
                })}
                className="input-field"
                placeholder="e.g., N12345, G-ABCD"
                style={{ textTransform: 'uppercase' }}
              />
              {errors.aircraft_id && (
                <p className="mt-1 text-sm text-red-600">{errors.aircraft_id.message}</p>
              )}
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Aircraft Type *
              </label>
              <div className="relative">
                <input
                  type="text"
                  {...register('aircraft_type', { required: 'Aircraft type is required' })}
                  className="input-field pr-10"
                  placeholder="e.g., C172, PA28, B737"
                  onChange={(e) => {
                    setValue('aircraft_type', e.target.value);
                    searchAircraftTypes(e.target.value);
                  }}
                  onBlur={handleBlur}
                />
                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                
                {/* Aircraft type suggestions dropdown */}
                {showTypeSuggestions && (
                  <div className="absolute z-20 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto top-full mt-1">
                    {searchingTypes ? (
                      <div className="px-3 py-2 text-center text-gray-500">Searching...</div>
                    ) : typeSuggestions.length > 0 ? (
                      typeSuggestions.map((type) => (
                        <button
                          key={type.id}
                          type="button"
                          className="w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100"
                          onClick={() => selectAircraftType(type)}
                        >
                          <div className="font-medium">{type.type_code}</div>
                          <div className="text-sm text-gray-600">
                            {type.manufacturer} {type.model}
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-center text-gray-500">
                        No matching aircraft types found
                      </div>
                    )}
                  </div>
                )}
              </div>
              {errors.aircraft_type && (
                <p className="mt-1 text-sm text-red-600">{errors.aircraft_type.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Start typing to see suggestions from database
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Manufacturer
              </label>
              <input
                type="text"
                {...register('manufacturer')}
                className="input-field"
                placeholder="e.g., Cessna, Piper, Boeing"
              />
              <p className="mt-1 text-xs text-gray-500">
                Auto-populated when selecting aircraft type or entering manufacturer/model
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Model
              </label>
              <input
                type="text"
                {...register('model')}
                className="input-field"
                placeholder="e.g., Skyhawk, Cherokee, 737-800"
              />
              <p className="mt-1 text-xs text-gray-500">
                Auto-populated when selecting aircraft type or entering manufacturer/model
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Year
              </label>
              <input
                type="number"
                {...register('year', {
                  min: { value: 1900, message: 'Year must be 1900 or later' },
                  max: { value: new Date().getFullYear() + 5, message: 'Year cannot be more than 5 years in the future' }
                })}
                className="input-field"
                placeholder="e.g., 2010"
                min="1900"
                max={new Date().getFullYear() + 5}
              />
              {errors.year && (
                <p className="mt-1 text-sm text-red-600">{errors.year.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Registration Country
              </label>
              <select {...register('registration_country')} className="input-field">
                <option value="">Select Country</option>
                <option value="US">United States (N)</option>
                <option value="CA">Canada (C)</option>
                <option value="GB">United Kingdom (G)</option>
                <option value="AU">Australia (VH)</option>
                <option value="DE">Germany (D)</option>
                <option value="FR">France (F)</option>
                <option value="IT">Italy (I)</option>
                <option value="JP">Japan (JA)</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Engine and Performance */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Engine & Performance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Engine Type
              </label>
              <select {...register('engine_type')} className="input-field">
                <option value="piston">Piston</option>
                <option value="turboprop">Turboprop</option>
                <option value="turbojet">Turbojet</option>
                <option value="jet">Jet</option>
                <option value="electric">Electric</option>
                <option value="turbofan">Turbofan</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Auto-populated when selecting aircraft type or matching manufacturer/model
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                {...register('high_performance')}
                className="h-4 w-4 text-aviation-600 focus:ring-aviation-500 border-gray-300 rounded"
              />
              <label className="text-sm text-gray-900">
                High Performance (>200 HP)
              </label>
            </div>
          </div>
        </div>

        {/* Aircraft Characteristics */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Aircraft Characteristics</h2>
          <p className="text-sm text-gray-600 mb-4">
            These will be auto-populated when selecting a known aircraft type from the database or matching manufacturer/model
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register('complex')}
                  className="h-4 w-4 text-aviation-600 focus:ring-aviation-500 border-gray-300 rounded"
                />
                <label className="text-sm text-gray-900">
                  Complex Aircraft
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register('retractable_gear')}
                  className="h-4 w-4 text-aviation-600 focus:ring-aviation-500 border-gray-300 rounded"
                />
                <label className="text-sm text-gray-900">
                  Retractable Landing Gear
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register('constant_speed_prop')}
                  className="h-4 w-4 text-aviation-600 focus:ring-aviation-500 border-gray-300 rounded"
                />
                <label className="text-sm text-gray-900">
                  Constant Speed Propeller
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register('pressurized')}
                  className="h-4 w-4 text-aviation-600 focus:ring-aviation-500 border-gray-300 rounded"
                />
                <label className="text-sm text-gray-900">
                  Pressurized
                </label>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register('glass_cockpit')}
                  className="h-4 w-4 text-aviation-600 focus:ring-aviation-500 border-gray-300 rounded"
                />
                <label className="text-sm text-gray-900">
                  Glass Cockpit
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register('turbo_charged')}
                  className="h-4 w-4 text-aviation-600 focus:ring-aviation-500 border-gray-300 rounded"
                />
                <label className="text-sm text-gray-900">
                  Turbo Charged
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Fields */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Aircraft Classification</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <input
                type="text"
                {...register('category')}
                className="input-field"
                placeholder="e.g., Airplane, Helicopter"
              />
              <p className="mt-1 text-xs text-gray-500">
                Auto-populated when available
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Class
              </label>
              <input
                type="text"
                {...register('class')}
                className="input-field"
                placeholder="e.g., Single Engine Land, Multi-Engine Land"
              />
              <p className="mt-1 text-xs text-gray-500">
                Auto-populated when available
              </p>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Additional Information</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              {...register('notes')}
              rows={4}
              className="input-field"
              placeholder="Additional notes about this aircraft, equipment, special procedures, etc."
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/aircraft')}
            className="btn-secondary flex items-center space-x-2"
          >
            <X className="h-4 w-4" />
            <span>Cancel</span>
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{loading ? 'Saving...' : 'Save Aircraft'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddAircraft;